import { Add, Edit, Settings, WarningAmberOutlined, WorkOutline } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Network from '../../../../redux/actions/network.actions';
import moment from 'moment-mini';
import { isJobPending } from '../../../../redux/selectors';
import * as SourceUtils from '../../../availableSources/SourceCardUtil';
import * as TargetUtils from '../../../targets/TargetCardUtil';
import * as ConnectorUtils from '../../../availableConnectorsV2/ConnectorCardUtil';


const EntityCard = ({ entity, onClickAction, entityType, isLinked, flowId, operation }) => {
  const processingOAuth = useSelector(state => state.flows.processingOAuth)
  const selectedEntity = useSelector(state => state.entities.selectedEntity)
  const selectedFlow = useSelector(state => state.flows.selectedFlow)
  const linkedSources = useSelector(state => state.sources.linked)
  const linkedConnectors = useSelector(state => state.connectors.linked)
  const tenant = useSelector(state => state.configuration.tenant_id)
  const enableJobPollIndicator = useSelector(state => state.preferences.enableJobPollIndicator)
  const lookupKey = useMemo(() => {
    if (entityType === 'source')
      return 'tap'
    if (entityType === 'target')
      return 'target'
    if (entityType === 'connector')
      return 'id'
  }, [entityType])
  const lastJob = useSelector((state) => state.jobs.lastJob[`${tenant}/${flowId}/${entity[lookupKey]}`]);
  const pendingJob = useSelector((state) => isJobPending(state, lastJob?.s3_root, flowId));
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [jobPending, setJobPending] = useState(false)

  const shouldDisable = useSelector(state => state.ui.shouldDisable ?? (() => false))


  const isDisabled = useMemo(() => shouldDisable(flowId, entity?.[lookupKey]), [shouldDisable, lookupKey])

  useEffect(() => {
    if (selectedEntity && selectedFlow) {
      if (entityType === 'source') {
        setIsLoading(selectedEntity.tap === entity.tap && selectedFlow === flowId)
      }

      if (entityType === 'target') {
        setIsLoading(selectedEntity.target === entity.target && selectedFlow === flowId)
      }

      if (entityType === 'connector') {
        setIsLoading(selectedEntity.id === entity.id && selectedFlow === flowId)
      }
    }
    // if flow isn't setted and selectedEntity isn't setted
    if(!selectedEntity || !selectedFlow) {
      setIsLoading(false)
    }

  }, [selectedEntity, selectedFlow])

  useEffect(() => {
    if (isLinked) {
      if (!linkedSources && entityType === 'source' && selectedEntity?.[lookupKey] === entity?.[lookupKey] && selectedFlow === flowId) {
        dispatch(Network.fetchLinkedSourceByFlowAndTenant(entity.tap, flowId, tenant))
      }

      if (!linkedConnectors && entityType === 'connector' && selectedEntity?.[lookupKey] === entity?.[lookupKey] && selectedFlow === flowId) {
        dispatch(Network.fetchLinkedConnectorByFlowAndTenant(entity.id, flowId, tenant))
      }

      setIsLoading(false);
    }
  }, [isLinked, linkedSources, linkedConnectors, setIsLoading, selectedEntity, selectedFlow])

  useEffect(() => {
    if (flowId && (!lastJob || lastJob.flow_id !== flowId)) {
      // If there is no last job, check if there is one
      dispatch(Network.fetchJob(flowId, tenant, entity[lookupKey], entityType));
    }
  }, [lastJob]);

  useEffect(() => {
    if (
      !pendingJob &&
      lastJob &&
      lastJob.status &&
      lastJob.s3_root.split('/')[0] === entity.tenant_id &&
      !(lastJob.status.includes('FAILED') || lastJob.status.includes('COMPLETE'))
    ) {
      setJobPending(true);
      handlePollJob(lastJob.s3_root);
    }
  }, [lastJob]);

  useEffect(() => {
    const entityKey = lookupKey === 'id' ? 'connector_id' : lookupKey;
    lastJob && entity && lookupKey && lastJob[entityKey] === entity[lookupKey] && setJobPending(jobPending);
  }, [jobPending]);

  const handlePollJob = (jobRoot) => {
    if (entityType === 'connector') {
      return ConnectorUtils.pollJob(jobRoot, flowId, tenant);
    } else if (entityType === 'source'){
      return SourceUtils.pollJob(jobRoot, flowId, tenant);
    } else {
      return TargetUtils.pollJob(jobRoot, flowId, tenant);
    }
  };

  return (
    <Box
      className={
        'hg-connector-card ' + (isLinked && 'hg-connector-card-linked') + (isDisabled && 'hg-connector-card-disabled')
      }
      display="flex"
      justifyContent="space-between"
      pl={3}
      pr={2}
      py={2}
      borderRadius="5px"
      minWidth={200}
      sx={{
        border: ({ palette }) => `1px solid ${palette.common.outline}`,
        filter: isDisabled ? 'grayscale(100%)' : 'none',
        pointerEvents: isDisabled ? 'none' : 'auto',
      }}
      gap={4}
    >
      <Box display="flex" gap={2} alignItems="center">
        <Box
          className="hg-connector-logo"
          component="img"
          role='img'
          aria-label={`${entity.label} icon`}
          alt={`${entity.label} icon`}
          src={entity.icon}
          width={28}
          height={28}
        />
        <Box display="flex" gap={1} alignItems="center">
          <Typography
            className="hg-connector-name"
            fontWeight="500"
            variant="body1"
            sx={{
              color: ({ palette }) => isDisabled && palette.text.disabled,
            }}
          >
            {entity.label}
          </Typography>
          {isLinked && (
            <Box
              className="hg-connector-linked"
              aria-label={`linked to ${entity.label}`}
              role="status"
              display="flex"
              bgcolor="success.light"
              borderRadius="10px"
              mr={1}
              alignItems="center"
              justifyContent="center"
              height="20px"
            >
              <Typography
                variant="body2"
                fontWeight="700"
                fontSize="9px"
                color="success.dark"
                sx={{ px: 1, py: '2px' }}
                aria-hidden="true"
              >
                LINKED
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box display="flex" gap={1} alignItems={'center'}>
        {enableJobPollIndicator && (
          <Box display="flex" alignItems="end" minWidth="29px">
            {jobPending ? (
              <Tooltip title={`Job Running`} placement="top">
                <Box>
                  <Box
                    width="28px"
                    role="status"
                    height="28px"
                    sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <WorkOutline sx={{ fontSize: '13px', position: 'absolute' }} />

                    <CircularProgress
                      size={24}
                      aria-label="Job Running"
                      sx={{
                        color: '#FA5240',
                        position: 'absolute',
                        zIndex: 1,
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            ) : lastJob ? (
              lastJob.status === 'JOB_COMPLETED' ? (
                <Tooltip
                  title={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #36C53C"
                    width={28}
                    height={28}
                    borderRadius="32px"
                    display="flex"
                    aria-label={`Last synced at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    role="status"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <WorkOutline sx={{ fontSize: '16px' }} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip
                  title={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                  placement="top"
                >
                  <Box
                    border="1px solid #FA2A52"
                    width={28}
                    height={28}
                    borderRadius="32px"
                    display="flex"
                    role="status"
                    aria-label={`Sync failed at ${moment(lastJob.start_time).format('HH:mm MM/DD/YY')}`}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <WarningAmberOutlined sx={{ fontSize: '16px', color: '#FA2A52' }} />
                  </Box>
                </Tooltip>
              )
            ) : (
              ``
            )}
          </Box>
        )}
        <IconButton
          role='button'
          aria-label={
            isLinked
              ? `${entity.label} connector settings button, opens in a modal`
              : entity.type === 'oauth'
              ? `button for linking ${entity.label} connector, opens in a new window`
              : `button for linking ${entity.label} connector, opens in a modal`
          }
          className="hg-connector-settings"
          onClick={onClickAction}
          disabled={processingOAuth || isLoading || (!isLinked && operation === 'unlink')}
        >
          {isLoading ? (
            <CircularProgress size={14} />
          ) : isLinked ? (
            <Settings sx={{ fontSize: '16px' }} />
          ) : (
            <Add sx={{ fontSize: '16px' }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default EntityCard;
